import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Copy, Check, X, Edit3, Trash2, FileDown, BarChart2, User as UserIcon, BadgeCheck, Inbox, ThumbsUp, ThumbsDown, Instagram, Facebook, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail, formatPrice, API } from "../../lib/api";

// ---------------------------------------------------------------------------
// Admin → Herramientas → Socios (Partners / Affiliates)
// - Admin creates partner codes linked to registered users.
// - Two modes per partner: "tracking" (only records the use) or
//   "commission" (accumulates commission_rate% of subtotal on paid orders).
// - Each partner has stats and a details modal listing every use with the
//   ability to mark commissions as paid.
// ---------------------------------------------------------------------------
export default function SociosAdmin() {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [detailFor, setDetailFor] = useState(null);
    const [editing, setEditing] = useState(null);
    const [pendingApps, setPendingApps] = useState([]);
    const [reviewApp, setReviewApp] = useState(null); // application under admin review

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [partnersRes, appsRes] = await Promise.all([
                api.get("/admin/partners"),
                api.get("/admin/partner-applications?status=pending").catch(() => ({ data: [] })),
            ]);
            setPartners(partnersRes.data);
            setPendingApps(appsRes.data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error cargando socios");
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const removePartner = async (p) => {
        if (!window.confirm(`¿Eliminar al socio "${p.display_name}" (código ${p.code})? Sus registros históricos se conservarán en los reportes.`)) return;
        try {
            await api.delete(`/admin/partners/${p.id}`);
            toast.success("Socio eliminado");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };

    const copyCode = (code) => {
        try { navigator.clipboard.writeText(code); toast.success(`Copiado: ${code}`); }
        catch { toast.error("No se pudo copiar"); }
    };

    const downloadReport = async () => {
        try {
            const token = localStorage.getItem("tb_token");
            const url = `${API}/admin/partners/report.xlsx`;
            const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!resp.ok) throw new Error(await resp.text());
            const blob = await resp.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `socios-reporte-${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
        } catch (e) { toast.error("Error al descargar reporte"); }
    };

    return (
        <div className="space-y-4" data-testid="socios-admin-panel">
            {/* Intro */}
            <div className="tb-card p-4">
                <h3 className="font-display text-lg mb-1">Códigos de Socio (Afiliados)</h3>
                <p className="text-xs text-[var(--tb-text-2)] leading-relaxed">
                    Crea códigos únicos vinculados a un usuario registrado. Cuando un comprador usa el código en checkout,
                    se registra automáticamente la venta bajo ese socio. Los códigos <strong>no dan descuento</strong> —
                    son solo para tracking y recompensa manual. Los socios <em>públicos</em> aparecen en la página de
                    Vendedores para reconocimiento; los socios pueden ver sus propias métricas desde su perfil.
                </p>
            </div>

            {/* Pending applications — highlighted at the top so admin doesn't miss them. */}
            {pendingApps.length > 0 && (
                <div className="tb-card p-4 border border-[var(--tb-gold)]/50 bg-[var(--tb-gold)]/5" data-testid="pending-apps-block">
                    <div className="flex items-center gap-2 mb-3">
                        <Inbox className="w-4 h-4 text-[var(--tb-gold)]" />
                        <h3 className="font-display text-lg">Postulaciones pendientes ({pendingApps.length})</h3>
                    </div>
                    <div className="space-y-2">
                        {pendingApps.map((a) => (
                            <div key={a.id} className="flex items-start gap-3 p-3 rounded bg-[var(--tb-bg)] border border-[var(--tb-border)]" data-testid={`pending-app-${a.id}`}>
                                {a.user_photo ? <img src={a.user_photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                                    : <div className="w-10 h-10 rounded-full bg-[var(--tb-bg-2)] flex items-center justify-center flex-shrink-0"><UserIcon className="w-5 h-5 text-[var(--tb-muted)]" /></div>}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm flex items-center gap-1 flex-wrap">
                                        <strong>{a.user_public_name || a.user_name}</strong>
                                        {a.user_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#2196F3]" />}
                                        <span className="font-mono text-[10px] text-[var(--tb-gold)] bg-[var(--tb-gold)]/15 px-1.5 py-0.5 rounded">{a.code_suggested}</span>
                                        <span className="text-[10px] text-[var(--tb-muted)] ml-auto">{new Date(a.created_at).toLocaleDateString("es-SV")}</span>
                                    </div>
                                    <p className="text-xs text-[var(--tb-text-2)] mt-1 line-clamp-2">{a.motivation}</p>
                                    {(a.audience_size || Object.keys(a.social_links || {}).length > 0) && (
                                        <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] text-[var(--tb-muted)]">
                                            {a.audience_size && <span>👥 {a.audience_size}</span>}
                                            {a.social_links?.instagram && <span className="inline-flex items-center gap-1"><Instagram className="w-3 h-3" /> {a.social_links.instagram}</span>}
                                            {a.social_links?.tiktok && <span>TT: {a.social_links.tiktok}</span>}
                                            {a.social_links?.facebook && <span className="inline-flex items-center gap-1"><Facebook className="w-3 h-3" /> {a.social_links.facebook}</span>}
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => setReviewApp(a)} className="tb-btn-primary !text-xs whitespace-nowrap" data-testid={`review-app-${a.id}`}>
                                    Revisar
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Actions bar */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-[var(--tb-text-2)]">
                    {loading ? "Cargando…" : `${partners.length} socio${partners.length === 1 ? "" : "s"}`}
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={downloadReport} className="tb-btn-secondary !text-xs inline-flex items-center gap-1" data-testid="socios-export">
                        <FileDown className="w-3.5 h-3.5" /> Excel
                    </button>
                    <button onClick={() => setShowCreate(true)} className="tb-btn-primary !text-xs inline-flex items-center gap-1" data-testid="socios-new">
                        <Plus className="w-3.5 h-3.5" /> Nuevo socio
                    </button>
                </div>
            </div>

            {/* Cards grid */}
            {partners.length === 0 && !loading ? (
                <div className="tb-card p-8 text-center text-sm text-[var(--tb-text-2)]">
                    Aún no hay socios. Crea el primero para empezar a rastrear ventas por afiliado.
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-3">
                    {partners.map((p) => (
                        <PartnerCard key={p.id} p={p}
                            onCopy={copyCode}
                            onDetails={() => setDetailFor(p)}
                            onEdit={() => setEditing(p)}
                            onDelete={() => removePartner(p)} />
                    ))}
                </div>
            )}

            {showCreate && <CreatePartnerModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
            {editing && <EditPartnerModal p={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {detailFor && <PartnerDetailModal p={detailFor} onClose={() => setDetailFor(null)} onChanged={load} />}
            {reviewApp && <ReviewApplicationModal app={reviewApp} onClose={() => setReviewApp(null)} onDone={() => { setReviewApp(null); load(); }} />}
        </div>
    );
}


// ---------------------------------------------------------------------------
// Application review modal — admin approves (with commission decisions) or rejects.
// ---------------------------------------------------------------------------
function ReviewApplicationModal({ app, onClose, onDone }) {
    const [mode, setMode] = useState("tracking");
    const [rate, setRate] = useState(10);
    const [code, setCode] = useState(app.code_suggested || "");
    const [isPublic, setIsPublic] = useState(true);
    const [adminNote, setAdminNote] = useState("");
    const [rejectMode, setRejectMode] = useState(false);
    const [saving, setSaving] = useState(false);
    // Resolve departamento/municipio names from IDs via the public C807 catalog.
    const [deptoName, setDeptoName] = useState("");
    const [muniName, setMuniName] = useState("");
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!app.departamento_id) return;
            try {
                const { data: deptos } = await api.get("/c807-public/catalogo/departamentos");
                const dlist = Array.isArray(deptos) ? deptos : (deptos?.items || []);
                const d = dlist.find((x) => String(x.id) === String(app.departamento_id));
                if (!cancelled && d) setDeptoName(d.nombre);
                if (app.municipio_id) {
                    const { data: munis } = await api.get(`/c807-public/catalogo/municipios?departamento=${app.departamento_id}`);
                    const mlist = Array.isArray(munis) ? munis : (munis?.items || []);
                    const m = mlist.find((x) => String(x.id) === String(app.municipio_id));
                    if (!cancelled && m) setMuniName(m.nombre);
                }
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [app.departamento_id, app.municipio_id]);

    const approve = async () => {
        if (!code.trim() || code.trim().length < 3) {
            toast.error("El código debe tener al menos 3 caracteres");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/partner-applications/${app.id}/approve`, {
                code: code.trim(),
                commission_mode: mode,
                commission_rate: mode === "commission" ? Number(rate) : 0,
                is_public: isPublic,
                admin_note: adminNote,
            });
            toast.success(`Socio ${code.toUpperCase()} creado`);
            onDone?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al aprobar"); }
        finally { setSaving(false); }
    };

    const reject = async () => {
        if (adminNote.trim().length < 5) {
            toast.error("Explica brevemente el motivo del rechazo");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/partner-applications/${app.id}/reject`, { admin_note: adminNote });
            toast.success("Postulación rechazada");
            onDone?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al rechazar"); }
        finally { setSaving(false); }
    };

    return (
        <div className="tb-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="tb-modal-content !max-w-2xl" data-testid="review-app-modal">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h3 className="font-display text-2xl">Revisar postulación</h3>
                        <div className="text-xs text-[var(--tb-muted)] flex items-center gap-1">
                            {app.user_public_name || app.user_name}
                            {app.user_verified && <BadgeCheck className="w-3 h-3 text-[#2196F3]" />}
                            · {app.user_email}
                        </div>
                    </div>
                    <button onClick={onClose} className="tb-btn-ghost !p-2"><X className="w-4 h-4" /></button>
                </div>

                {/* Application details (read-only) */}
                <div className="space-y-2 text-sm bg-[var(--tb-bg-2)]/40 p-3 rounded border border-[var(--tb-border)]">
                    <div><strong className="text-[var(--tb-gold)]">Código sugerido:</strong> <span className="font-mono">{app.code_suggested}</span></div>
                    <div><strong className="text-[var(--tb-gold)]">Motivación (privada):</strong> {app.motivation}</div>
                    {app.audience_size && <div><strong className="text-[var(--tb-gold)]">Audiencia:</strong> {app.audience_size}</div>}
                    {app.promotion_plan && <div><strong className="text-[var(--tb-gold)]">Plan:</strong> {app.promotion_plan}</div>}
                    {app.contact_phone && <div><strong className="text-[var(--tb-gold)]">Teléfono:</strong> {app.contact_phone}</div>}
                    {(app.bank_name || app.bank_account_number) && (
                        <div className="mt-2 pt-2 border-t border-[var(--tb-border)] bg-[var(--tb-bg)]/50 p-2 rounded">
                            <div className="text-[10px] text-[var(--tb-gold)] uppercase tracking-wider mb-1">🔒 Datos bancarios</div>
                            <div className="text-xs space-y-0.5">
                                {app.bank_name && <div>Banco: <strong>{app.bank_name}</strong></div>}
                                {app.bank_account_type && <div>Tipo: {app.bank_account_type}</div>}
                                {app.bank_account_number && <div className="font-mono">Cuenta: {app.bank_account_number}</div>}
                                {app.bank_account_holder && <div>Titular: {app.bank_account_holder}</div>}
                            </div>
                        </div>
                    )}
                    {(app.address_street || app.departamento_id) && (
                        <div className="mt-2 pt-2 border-t border-[var(--tb-border)] bg-[var(--tb-bg)]/50 p-2 rounded">
                            <div className="text-[10px] text-[var(--tb-gold)] uppercase tracking-wider mb-1">🔒 Dirección</div>
                            <div className="text-xs space-y-0.5">
                                {app.address_street && <div>{app.address_street}</div>}
                                {(deptoName || muniName) && (
                                    <div>{[muniName, deptoName].filter(Boolean).join(", ")}{deptoName ? ", El Salvador" : ""}</div>
                                )}
                                {app.address_reference && <div className="text-[var(--tb-muted)] italic">Ref: {app.address_reference}</div>}
                            </div>
                        </div>
                    )}
                    {(app.document_type || app.document_number) && (
                        <div className="mt-2 pt-2 border-t border-[var(--tb-border)] bg-[var(--tb-bg)]/50 p-2 rounded">
                            <div className="text-[10px] text-[var(--tb-gold)] uppercase tracking-wider mb-1">🔒 Documento de identidad</div>
                            <div className="text-xs">
                                <strong>{app.document_type || "—"}:</strong> <span className="font-mono">{app.document_number || "—"}</span>
                            </div>
                        </div>
                    )}
                    {Object.keys(app.social_links || {}).length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {app.social_links.instagram && <span className="inline-flex items-center gap-1 text-xs"><Instagram className="w-3 h-3" /> {app.social_links.instagram}</span>}
                            {app.social_links.tiktok && <span className="inline-flex items-center gap-1 text-xs"><ExternalLink className="w-3 h-3" /> TikTok: {app.social_links.tiktok}</span>}
                            {app.social_links.facebook && <span className="inline-flex items-center gap-1 text-xs"><Facebook className="w-3 h-3" /> {app.social_links.facebook}</span>}
                        </div>
                    )}
                </div>

                {!rejectMode ? (
                    <div className="space-y-3 mt-4">
                        <h4 className="font-display">Aprobar</h4>
                        <div>
                            <label className="tb-overline text-[var(--tb-gold)]">Código final</label>
                            <input className="tb-input mt-1 uppercase font-mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
                            <p className="text-[10px] text-[var(--tb-muted)] mt-1">Puedes modificar el código sugerido antes de aprobar.</p>
                        </div>
                        <div>
                            <label className="tb-overline text-[var(--tb-gold)]">Modo</label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                <button type="button" onClick={() => setMode("tracking")}
                                    className={`p-3 border text-left rounded transition ${mode === "tracking" ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/10" : "border-[var(--tb-border)]"}`}>
                                    <div className="text-sm font-medium">Solo tracking</div>
                                    <div className="text-[10px] text-[var(--tb-muted)]">Registra las ventas.</div>
                                </button>
                                <button type="button" onClick={() => setMode("commission")}
                                    className={`p-3 border text-left rounded transition ${mode === "commission" ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/10" : "border-[var(--tb-border)]"}`}>
                                    <div className="text-sm font-medium">Con comisión %</div>
                                    <div className="text-[10px] text-[var(--tb-muted)]">Acumula % del subtotal.</div>
                                </button>
                            </div>
                        </div>
                        {mode === "commission" && (
                            <div>
                                <label className="tb-overline text-[var(--tb-gold)]">Porcentaje</label>
                                <input type="number" min="0.5" max="90" step="0.5" className="tb-input mt-1" value={rate} onChange={(e) => setRate(e.target.value)} />
                            </div>
                        )}
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                            <span>Aparecer en /vendedores</span>
                        </label>
                        <div>
                            <label className="tb-overline text-[var(--tb-gold)]">Nota interna (opcional)</label>
                            <input className="tb-input mt-1" value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                                placeholder="Ej. buen alcance, aprobado sin duda" />
                        </div>
                        <div className="flex gap-2 justify-between pt-3 border-t border-[var(--tb-border)]">
                            <button type="button" onClick={() => setRejectMode(true)} className="tb-btn-ghost !text-sm text-rose-400 inline-flex items-center gap-1" data-testid="review-reject-btn">
                                <ThumbsDown className="w-3.5 h-3.5" /> Rechazar en su lugar
                            </button>
                            <button type="button" onClick={approve} disabled={saving} className="tb-btn-primary inline-flex items-center gap-1 disabled:opacity-50" data-testid="review-approve-btn">
                                <ThumbsUp className="w-3.5 h-3.5" /> {saving ? "..." : "Aprobar y crear socio"}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 mt-4">
                        <h4 className="font-display text-rose-300">Rechazar</h4>
                        <div>
                            <label className="tb-overline">Motivo (será enviado al postulante por email)</label>
                            <textarea className="tb-input mt-1 min-h-[80px]" value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                                placeholder="Ej. Por ahora buscamos socios con audiencia > 3k. Vuelve a postular cuando tengas más alcance." required data-testid="review-reject-note" />
                        </div>
                        <div className="flex gap-2 justify-between pt-3 border-t border-[var(--tb-border)]">
                            <button type="button" onClick={() => setRejectMode(false)} className="tb-btn-secondary !text-sm">Volver</button>
                            <button type="button" onClick={reject} disabled={saving} className="tb-btn-primary bg-rose-500 hover:bg-rose-600 disabled:opacity-50" data-testid="review-reject-submit">
                                {saving ? "..." : "Confirmar rechazo"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// One partner summary card
// ---------------------------------------------------------------------------
function PartnerCard({ p, onCopy, onDetails, onEdit, onDelete }) {
    const stats = p.stats || {};
    const modeBadge = p.commission_mode === "commission"
        ? `Comisión ${Number(p.commission_rate || 0)}%`
        : "Solo tracking";
    const due = Number(stats.commission_due_now || 0);
    const closeCycle = async () => {
        if (due <= 0) { toast.error("No hay comisiones confirmadas pendientes"); return; }
        if (!window.confirm(`¿Cerrar ciclo y marcar como pagados ${formatPrice(due)} a ${p.display_name}? Esta acción se registra en el historial y reinicia el contador semanal.`)) return;
        try {
            const { data } = await api.post(`/admin/partners/${p.id}/close-cycle`);
            toast.success(`Ciclo cerrado: ${data.uses_closed} pedidos · ${formatPrice(data.amount_paid)}`);
            window.location.reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    return (
        <div className={`tb-card p-4 ${p.active ? "" : "opacity-60"}`} data-testid={`socio-card-${p.code}`}>
            <div className="flex items-start gap-3">
                {p.photo ? (
                    <img src={p.photo} alt="" className="w-11 h-11 rounded-full object-cover border border-[var(--tb-border)]" />
                ) : (
                    <div className="w-11 h-11 rounded-full bg-[var(--tb-bg-2)] flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-[var(--tb-muted)]" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                        <div className="font-medium truncate">{p.display_name || "—"}</div>
                        {p.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#2196F3] fill-[#2196F3]/20" />}
                        {!p.is_public && <span className="text-[9px] uppercase tracking-wider text-[var(--tb-muted)] bg-[var(--tb-bg-2)] px-1.5 py-0.5 rounded">Privado</span>}
                        {!p.active && <span className="text-[9px] uppercase tracking-wider text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded">Inactivo</span>}
                    </div>
                    <div className="text-[11px] text-[var(--tb-muted)] truncate">{p.email}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <button onClick={() => onCopy(p.code)} className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 bg-[var(--tb-gold)]/15 border border-[var(--tb-gold)]/40 rounded hover:bg-[var(--tb-gold)]/25 transition" title="Copiar código">
                        <Copy className="w-3 h-3" /> {p.code}
                    </button>
                    <span className="text-[10px] text-[var(--tb-muted)]">{modeBadge}</span>
                </div>
            </div>

            <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-[var(--tb-border)] text-center">
                <StatBlock label="Confirmadas" value={stats.uses_confirmed || 0} />
                <StatBlock label="Pendientes" value={stats.uses_pending || 0} muted />
                <StatBlock label="Ingresos" value={formatPrice(stats.revenue_confirmed || 0)} accent />
                <StatBlock label="Debe ahora" value={formatPrice(due)} accent />
            </div>

            {/* Weekly cycle close — only shown when commission_mode='commission' AND there's a real balance. */}
            {p.commission_mode === "commission" && (
                <button onClick={closeCycle}
                    disabled={due <= 0}
                    className={`w-full mt-2 !text-[11px] !py-1.5 rounded inline-flex items-center justify-center gap-1 border transition ${due > 0 ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/30" : "bg-[var(--tb-bg-2)]/30 border-[var(--tb-border)] text-[var(--tb-muted)] cursor-not-allowed"}`}
                    data-testid={`socio-close-cycle-${p.code}`}
                    title={due > 0 ? "Marca las comisiones confirmadas como pagadas y reinicia el contador" : "Sin comisión pendiente"}>
                    💸 Cerrar ciclo semanal · {formatPrice(due)}
                </button>
            )}

            <div className="flex gap-2 mt-3">
                <button onClick={onDetails} className="tb-btn-secondary !text-[11px] !py-1.5 inline-flex items-center gap-1 flex-1" data-testid={`socio-details-${p.code}`}>
                    <BarChart2 className="w-3 h-3" /> Ver métricas
                </button>
                <button onClick={onEdit} className="tb-btn-ghost !p-1.5" title="Editar" data-testid={`socio-edit-${p.code}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={onDelete} className="tb-btn-ghost !p-1.5 text-rose-400 hover:text-rose-300" title="Eliminar" data-testid={`socio-delete-${p.code}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

function StatBlock({ label, value, accent, muted }) {
    return (
        <div>
            <div className={`text-sm font-semibold ${accent ? "text-[var(--tb-gold)]" : muted ? "text-[var(--tb-muted)]" : ""}`}>{value}</div>
            <div className="text-[9px] text-[var(--tb-muted)] uppercase tracking-wider">{label}</div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// User picker (autocomplete) — used inside CreatePartnerModal.
// ---------------------------------------------------------------------------
function UserPicker({ selected, onSelect }) {
    const [q, setQ] = useState("");
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/admin/partners/candidates", { params: q ? { q } : {} });
                if (!cancelled) setCandidates(data);
            } finally { if (!cancelled) setLoading(false); }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [q]);

    if (selected) {
        return (
            <div className="flex items-center gap-2 p-2 rounded border border-[var(--tb-gold)]/40 bg-[var(--tb-gold)]/10">
                {selected.photo ? <img src={selected.photo} alt="" className="w-8 h-8 rounded-full object-cover" /> : <UserIcon className="w-8 h-8 text-[var(--tb-muted)]" />}
                <div className="flex-1 min-w-0">
                    <div className="text-sm truncate flex items-center gap-1">{selected.name} {selected.verified && <BadgeCheck className="w-3 h-3 text-[#2196F3]" />}</div>
                    <div className="text-[10px] text-[var(--tb-muted)] truncate">{selected.email} · {selected.role}</div>
                </div>
                <button onClick={() => onSelect(null)} className="tb-btn-ghost !p-1" aria-label="Cambiar" data-testid="socios-clear-user">
                    <X className="w-4 h-4" />
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tb-muted)] pointer-events-none" />
                <input className="tb-input !pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o email..." data-testid="socios-user-search" />
            </div>
            <div className="mt-2 max-h-60 overflow-y-auto border border-[var(--tb-border)] rounded divide-y divide-[var(--tb-border)]">
                {loading && <div className="p-3 text-xs text-[var(--tb-muted)]">Buscando…</div>}
                {!loading && candidates.length === 0 && <div className="p-3 text-xs text-[var(--tb-muted)]">Sin resultados. Los usuarios que ya son socios están ocultos.</div>}
                {candidates.map((u) => (
                    <button key={u.id} onClick={() => onSelect(u)} className="w-full text-left p-2 hover:bg-[var(--tb-bg-2)] flex items-center gap-2" data-testid={`socios-user-${u.id}`}>
                        {u.photo ? <img src={u.photo} alt="" className="w-7 h-7 rounded-full object-cover" /> : <UserIcon className="w-7 h-7 text-[var(--tb-muted)]" />}
                        <div className="flex-1 min-w-0">
                            <div className="text-sm flex items-center gap-1 truncate">{u.name} {u.verified && <BadgeCheck className="w-3 h-3 text-[#2196F3] flex-shrink-0" />}</div>
                            <div className="text-[10px] text-[var(--tb-muted)] truncate">{u.email} · {u.role}</div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Create Partner modal
// ---------------------------------------------------------------------------
function CreatePartnerModal({ onClose, onCreated }) {
    const [user, setUser] = useState(null);
    const [code, setCode] = useState("");
    const [mode, setMode] = useState("tracking");
    const [rate, setRate] = useState(5);
    const [isPublic, setIsPublic] = useState(true);
    const [bio, setBio] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!user) { toast.error("Selecciona un usuario"); return; }
        if (!code.trim()) { toast.error("Escribe un código"); return; }
        setSaving(true);
        try {
            await api.post("/admin/partners", {
                user_id: user.id,
                code: code.trim(),
                commission_mode: mode,
                commission_rate: mode === "commission" ? Number(rate) : 0,
                is_public: isPublic,
                bio,
            });
            toast.success(`Socio creado con código ${code.toUpperCase()}`);
            onCreated?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al crear socio");
        } finally { setSaving(false); }
    };

    return (
        <div className="tb-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="tb-modal-content" data-testid="socios-create-modal">
                <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display text-2xl">Nuevo Socio</h3>
                    <button onClick={onClose} className="tb-btn-ghost !p-2"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">1. Usuario vinculado *</label>
                        <div className="mt-2"><UserPicker selected={user} onSelect={setUser} /></div>
                    </div>

                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">2. Código de socio *</label>
                        <input className="tb-input mt-1 uppercase font-mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="Ej. JULIO25, KATTY, MODA-VIP" maxLength={30} data-testid="socios-code-input" />
                        <p className="text-[10px] text-[var(--tb-muted)] mt-1">Solo letras, números, - y _. Los compradores escribirán este código en checkout.</p>
                    </div>

                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">3. Modo</label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            <button type="button" onClick={() => setMode("tracking")}
                                className={`p-3 border text-left rounded transition ${mode === "tracking" ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/10" : "border-[var(--tb-border)] hover:border-[var(--tb-gold)]/60"}`}
                                data-testid="socios-mode-tracking">
                                <div className="text-sm font-medium">Solo tracking</div>
                                <div className="text-[10px] text-[var(--tb-muted)]">Registra las ventas. Tú decides la recompensa fuera del sistema.</div>
                            </button>
                            <button type="button" onClick={() => setMode("commission")}
                                className={`p-3 border text-left rounded transition ${mode === "commission" ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/10" : "border-[var(--tb-border)] hover:border-[var(--tb-gold)]/60"}`}
                                data-testid="socios-mode-commission">
                                <div className="text-sm font-medium">Con comisión %</div>
                                <div className="text-[10px] text-[var(--tb-muted)]">Acumula un % del subtotal de cada pedido pagado con el código.</div>
                            </button>
                        </div>
                    </div>

                    {mode === "commission" && (
                        <div>
                            <label className="tb-overline text-[var(--tb-gold)]">Porcentaje de comisión *</label>
                            <div className="flex items-center gap-2 mt-1">
                                <input type="number" min="0.5" max="90" step="0.5" className="tb-input" value={rate}
                                    onChange={(e) => setRate(e.target.value)} data-testid="socios-rate-input" />
                                <span className="text-lg text-[var(--tb-gold)]">%</span>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">4. Bio pública (opcional)</label>
                        <textarea className="tb-input mt-1 min-h-[60px]" value={bio} onChange={(e) => setBio(e.target.value)}
                            placeholder="Breve descripción que aparecerá en /vendedores si el socio es público." data-testid="socios-bio-input" />
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="socios-public-toggle" />
                        <span>Aparecer públicamente en <code className="text-[var(--tb-gold)]">/vendedores</code></span>
                    </label>

                    <div className="flex gap-2 justify-end pt-3 border-t border-[var(--tb-border)]">
                        <button type="button" onClick={onClose} className="tb-btn-secondary !text-sm">Cancelar</button>
                        <button type="submit" disabled={saving} className="tb-btn-primary !text-sm disabled:opacity-50" data-testid="socios-create-submit">
                            {saving ? "Guardando…" : "Crear socio"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Edit Partner modal (subset of fields; the linked user cannot change).
// ---------------------------------------------------------------------------
function EditPartnerModal({ p, onClose, onSaved }) {
    const [code, setCode] = useState(p.code);
    const [mode, setMode] = useState(p.commission_mode || "tracking");
    const [rate, setRate] = useState(Number(p.commission_rate) || 5);
    const [isPublic, setIsPublic] = useState(!!p.is_public);
    const [active, setActive] = useState(!!p.active);
    const [bio, setBio] = useState(p.bio || "");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch(`/admin/partners/${p.id}`, {
                code: code.trim(),
                commission_mode: mode,
                commission_rate: mode === "commission" ? Number(rate) : 0,
                is_public: isPublic,
                bio,
                active,
            });
            toast.success("Socio actualizado");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al actualizar");
        } finally { setSaving(false); }
    };

    return (
        <div className="tb-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="tb-modal-content" data-testid="socios-edit-modal">
                <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display text-2xl">Editar Socio · {p.display_name}</h3>
                    <button onClick={onClose} className="tb-btn-ghost !p-2"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">Código</label>
                        <input className="tb-input mt-1 uppercase font-mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
                    </div>
                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">Modo</label>
                        <select className="tb-input mt-1" value={mode} onChange={(e) => setMode(e.target.value)}>
                            <option value="tracking">Solo tracking</option>
                            <option value="commission">Con comisión %</option>
                        </select>
                    </div>
                    {mode === "commission" && (
                        <div>
                            <label className="tb-overline text-[var(--tb-gold)]">Porcentaje de comisión</label>
                            <input type="number" min="0.5" max="90" step="0.5" className="tb-input mt-1" value={rate}
                                onChange={(e) => setRate(e.target.value)} />
                        </div>
                    )}
                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">Bio pública</label>
                        <textarea className="tb-input mt-1 min-h-[60px]" value={bio} onChange={(e) => setBio(e.target.value)} />
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                        <span>Aparecer en /vendedores</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                        <span>Activo (los compradores pueden usar el código)</span>
                    </label>
                    <div className="flex gap-2 justify-end pt-3 border-t border-[var(--tb-border)]">
                        <button type="button" onClick={onClose} className="tb-btn-secondary !text-sm">Cancelar</button>
                        <button type="submit" disabled={saving} className="tb-btn-primary !text-sm disabled:opacity-50">
                            {saving ? "Guardando…" : "Guardar"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Details modal — full list of uses + mark commission paid/pending.
// ---------------------------------------------------------------------------
function PartnerDetailModal({ p, onClose, onChanged }) {
    const [uses, setUses] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/partners/${p.id}/uses`);
            setUses(data);
        } catch { toast.error("Error cargando usos"); }
        finally { setLoading(false); }
    }, [p.id]);
    useEffect(() => { load(); }, [load]);

    const togglePaid = async (u) => {
        try {
            const endpoint = u.commission_status === "paid" ? "mark-pending" : "mark-paid";
            await api.post(`/admin/partners/uses/${u._id}/${endpoint}`);
            toast.success(u.commission_status === "paid" ? "Marcada como pendiente" : "Marcada como pagada");
            load();
            onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };

    return (
        <div className="tb-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="tb-modal-content !max-w-4xl" data-testid="socios-detail-modal">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h3 className="font-display text-2xl">{p.display_name}</h3>
                        <div className="text-xs text-[var(--tb-muted)]">
                            <span className="font-mono text-[var(--tb-gold)]">{p.code}</span> · {p.email}
                        </div>
                    </div>
                    <button onClick={onClose} className="tb-btn-ghost !p-2"><X className="w-4 h-4" /></button>
                </div>

                {/* Stats summary */}
                {p.stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="tb-card p-3 text-center">
                            <div className="text-2xl font-semibold text-[var(--tb-gold)]">{p.stats.uses_confirmed}</div>
                            <div className="text-[10px] text-[var(--tb-muted)] uppercase">Ventas confirmadas</div>
                        </div>
                        <div className="tb-card p-3 text-center">
                            <div className="text-2xl font-semibold">{formatPrice(p.stats.revenue_confirmed)}</div>
                            <div className="text-[10px] text-[var(--tb-muted)] uppercase">Ingresos generados</div>
                        </div>
                        <div className="tb-card p-3 text-center">
                            <div className="text-2xl font-semibold text-emerald-400">{formatPrice(p.stats.commission_confirmed)}</div>
                            <div className="text-[10px] text-[var(--tb-muted)] uppercase">Comisión acumulada</div>
                        </div>
                        <div className="tb-card p-3 text-center">
                            <div className="text-2xl font-semibold text-amber-300">{p.stats.uses_pending}</div>
                            <div className="text-[10px] text-[var(--tb-muted)] uppercase">Pedidos pendientes</div>
                        </div>
                    </div>
                )}

                {/* Uses table */}
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="text-center py-8 text-sm text-[var(--tb-muted)]">Cargando…</div>
                    ) : uses.length === 0 ? (
                        <div className="text-center py-8 text-sm text-[var(--tb-muted)]">Este socio aún no tiene usos registrados.</div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-[10px] uppercase text-[var(--tb-muted)] border-b border-[var(--tb-border)]">
                                    <th className="p-2 text-left">Fecha</th>
                                    <th className="p-2 text-left">Pedido</th>
                                    <th className="p-2 text-left">Cliente</th>
                                    <th className="p-2 text-right">Subtotal</th>
                                    <th className="p-2 text-right">Comisión</th>
                                    <th className="p-2 text-center">Estado pedido</th>
                                    <th className="p-2 text-center">Estado comisión</th>
                                </tr>
                            </thead>
                            <tbody>
                                {uses.map((u) => (
                                    <tr key={u._id} className="border-b border-[var(--tb-border)] hover:bg-[var(--tb-bg-2)]/30">
                                        <td className="p-2 whitespace-nowrap">{new Date(u.created_at).toLocaleDateString("es-SV")}</td>
                                        <td className="p-2 font-mono">{(u.order_id || "").slice(-8)}</td>
                                        <td className="p-2">{u.customer_name}</td>
                                        <td className="p-2 text-right">{formatPrice(u.subtotal)}</td>
                                        <td className="p-2 text-right text-emerald-400">
                                            {u.commission_mode === "commission" ? formatPrice(u.commission_amount) : "—"}
                                        </td>
                                        <td className="p-2 text-center">
                                            <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                                u.effective_status === "confirmed" ? "bg-emerald-500/20 text-emerald-300" :
                                                u.effective_status === "cancelled" ? "bg-rose-500/20 text-rose-300" :
                                                "bg-amber-500/20 text-amber-300"
                                            }`}>{u.effective_status}</span>
                                        </td>
                                        <td className="p-2 text-center">
                                            {u.commission_mode === "commission" && u.effective_status === "confirmed" ? (
                                                <button onClick={() => togglePaid(u)}
                                                    className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition ${
                                                        u.commission_status === "paid" ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" :
                                                        "bg-[var(--tb-gold)]/20 text-[var(--tb-gold)] hover:bg-[var(--tb-gold)]/30"
                                                    }`} data-testid={`socios-toggle-paid-${u._id}`}>
                                                    {u.commission_status === "paid" ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 inline-block">⏳</span>}
                                                    {u.commission_status === "paid" ? "Pagada" : "Pendiente"}
                                                </button>
                                            ) : (
                                                <span className="text-[10px] text-[var(--tb-muted)]">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
